import { useState, useMemo } from 'react';
import { MagnifyingGlass, Buildings, X } from 'phosphor-react';
import {
  QuickbooksJob,
  QuickbooksInvoice,
  SupplierForBuyer,
  formatMoney,
  removeDuplicatesByProperty,
} from '@dill/dill-shared';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton, DillCheckbox } from '../../../general/Dill';

interface PayByProjectModalProps {
  open: boolean;
  onClose: () => void;
  supplier: SupplierForBuyer;
  onContinue: (jobIds: string[]) => void;
}

interface JobWithBalance extends QuickbooksJob {
  totalBalance: number;
  invoiceCount: number;
}

export const PayByProjectModal = ({
  open,
  onClose,
  supplier,
  onContinue,
}: PayByProjectModalProps) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const jobsWithBalance = useMemo((): JobWithBalance[] => {
    const allInvoices: QuickbooksInvoice[] = [
      ...(supplier?.invoices?.pastDueInvoices?.invoices || []),
      ...(supplier?.invoices?.currentInvoices?.invoices || []),
    ] as QuickbooksInvoice[];

    const rawJobs = allInvoices.reduce((prev: QuickbooksJob[], curr) => {
      if (curr.jobId && curr.quickBooksJob) {
        return [...prev, curr.quickBooksJob];
      }
      return prev;
    }, []);

    const uniqueJobs = removeDuplicatesByProperty({ inputArray: rawJobs, property: 'id' });

    return (uniqueJobs as QuickbooksJob[]).map((job) => {
      const jobInvoices = allInvoices.filter((inv) => inv.quickBooksJob?.id === job.id);
      return {
        ...job,
        totalBalance: jobInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0),
        invoiceCount: jobInvoices.length,
      };
    });
  }, [supplier?.invoices]);

  const filteredJobs = useMemo(() => {
    if (!search.trim()) return jobsWithBalance;
    const q = search.toLowerCase();
    return jobsWithBalance.filter(
      (job) =>
        job.jobName?.toLowerCase().includes(q) ||
        job.jobNumber?.toLowerCase().includes(q) ||
        job.address?.toLowerCase().includes(q)
    );
  }, [jobsWithBalance, search]);

  const toggleJob = (jobId: string) => {
    setSelectedIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === filteredJobs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredJobs.map((j) => j.id));
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedIds([]);
    onClose();
  };

  const handleContinue = () => {
    if (selectedIds.length === 0) return;
    onContinue(selectedIds);
    setSearch('');
    setSelectedIds([]);
  };

  const allSelected = filteredJobs.length > 0 && selectedIds.length === filteredJobs.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < filteredJobs.length;

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Pay by Project"
      subtitle="Select the jobs you want to pay. All open invoices for the selected jobs will be pre-selected."
      maxWidth="520px"
      contentClassName="flex-1 min-h-0 overflow-y-auto px-5 pb-4 pt-3 flex flex-col gap-3"
      footer={
        <>
          <button
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer bg-transparent border-none"
            onClick={handleClose}>
            Cancel
          </button>
          <DillButton
            onClick={handleContinue}
            isDisabled={selectedIds.length === 0}
            label={`Continue${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
          />
        </>
      }>
      {/* Search */}
      <div className="relative">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search by job name, number, or address…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 bg-gray-50"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-none p-0.5">
            <X size={14} />
          </button>
        )}
      </div>

      {/* No jobs state */}
      {jobsWithBalance.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
          <Buildings size={32} className="text-gray-300" />
          <p className="text-sm font-medium text-gray-500">No jobs found</p>
          <p className="text-xs text-gray-400">
            There are no open invoices associated with any project.
          </p>
        </div>
      ) : (
        <>
          {/* Select all row */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100"
            onClick={toggleAll}>
            <DillCheckbox isChecked={allSelected} indeterminate={someSelected} />
            <span className="text-xs font-medium text-gray-600">
              {allSelected ? 'Deselect all' : 'Select all'} ({filteredJobs.length})
            </span>
          </div>

          {/* Job list */}
          <div className="flex flex-col gap-1.5 overflow-y-auto max-h-[340px] pr-0.5">
            {filteredJobs.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">No jobs match your search.</p>
            ) : (
              filteredJobs.map((job) => {
                const isSelected = selectedIds.includes(job.id);
                const jobLabel = `${job.jobNumber ? `${job.jobNumber} – ` : ''}${job.jobName}`;
                const addressParts = [job.address, job.city, job.state].filter(Boolean);
                const address = addressParts.join(', ');

                return (
                  <div
                    key={job.id}
                    onClick={() => toggleJob(job.id)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                    }`}>
                    <DillCheckbox isChecked={isSelected} />
                    <div className="h-8 w-8 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
                      <Buildings size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{jobLabel}</p>
                      {address && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{address}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Selection summary */}
          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <span className="text-xs text-blue-700 font-medium">
                {selectedIds.length} job{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
            </div>
          )}
        </>
      )}
    </DillModal>
  );
};
